'use client';

import {Transition} from '@headlessui/react';
import {useId} from '@rocket-ui/react-tools';
import {
	cn,
	convertToHumanReadable,
	FileType,
	Measurement,
	omitEventDefaultBehavior,
	withEventBehaviourOmitted,
} from '@rocket-ui/tools';
import round from 'lodash/fp/round';
import React, {FC, Fragment, useState} from 'react';
import {Icon} from '../Icon';
import {Popover} from '../Popover';

import styles from './Input.module.css';

const dictionary: Record<FileType, string> = {
	[FileType.CSV]: '.csv',
	[FileType.PDF]: '.pdf',
	[FileType.PNG]: '.png',
	[FileType.JPEG]: '.jpeg',
	[FileType.XLS]: '.xls',
	[FileType.XLSX]: '.xlsx',
	[FileType.DOC]: '.doc',
	[FileType.DOCX]: '.docx',
};

export interface FileInputProps {
	value?: File | null;
	name?: string;
	onChange: (value: File | null) => void;
	placeholder?: string;
	replacePlaceholder?: string;
	acceptedFileTypes?: FileType[];
	loading?: boolean;
	removable?: boolean;
	onRemove?: () => void;
}

export const FileInput: FC<FileInputProps> = ({
	value,
	name,
	acceptedFileTypes,
	onChange,
	loading,
	placeholder,
	replacePlaceholder,
	removable,
	onRemove,
}) => {
	const [hovered, setHovered] = useState(false);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) =>
		e.target.files?.length && onChange(e.target.files[0]);

	const onOverHandler = (event: React.DragEvent<HTMLDivElement>): void => {
		omitEventDefaultBehavior(event);
		setHovered(true);
	};

	const onDropHandler = async (event: React.DragEvent<HTMLDivElement>): Promise<void> => {
		omitEventDefaultBehavior(event);
		setHovered(false);
		onChange(event.dataTransfer.files[0]);
	};

	const handleRemove = () => {
		onChange(null);
		onRemove?.();
	};

	return (
		<div
			className={'relative flex items-center justify-between w-full'}
			data-testid={'document-upload-form-field'}
			onDragEnter={omitEventDefaultBehavior}
			onDragOver={onOverHandler}
			onDragLeave={withEventBehaviourOmitted(() => setHovered(false))}
			onDragEnd={() => setHovered(false)}
			onDrop={onDropHandler}
		>
			{value ? (
				<SelectedFile
					name={name}
					value={value}
					loading={loading}
					onChange={handleChange}
					acceptedFileTypes={acceptedFileTypes}
					placeholder={replacePlaceholder || placeholder}
					removable={removable}
					onRemove={handleRemove}
				/>
			) : (
				<UploadNewFile
					name={name}
					onChange={handleChange}
					acceptedFileTypes={acceptedFileTypes}
					placeholder={placeholder}
				/>
			)}
			<DragOverlay hovered={hovered} />
		</div>
	);
};

const UploadNewFile: React.FC<{
	name?: string;
	placeholder?: string;
	acceptedFileTypes?: FileType[];
	onChange: React.ChangeEventHandler;
}> = ({name, placeholder, acceptedFileTypes, onChange}) => {
	const acceptedInfo = acceptedFileTypes?.map((it) => dictionary[it]).join(',');

	return (
		<div className={'flex justify-between items-center w-full h-full p-4'}>
			<FileInputFormField name={name} acceptedFileTypes={acceptedFileTypes} onChange={onChange}>
				<div className={'flex items-center gap-4 cursor-pointer'}>
					<span className={'flex justify-center items-center w-6 h-6 bg-black/[.04] rounded-md'}>
						<Icon name={'plus'} />
					</span>

					<h4>{placeholder}</h4>
				</div>
			</FileInputFormField>

			<p className={'text-black/25'}>{acceptedInfo}</p>
		</div>
	);
};

const measurementTranslation: Record<Measurement, string> = {
	[Measurement.BYTE]: 'б',
	[Measurement.KB]: 'Кб',
	[Measurement.MB]: 'Мб',
	[Measurement.GB]: 'Гб',
	[Measurement.TB]: 'Тб',
};

const FileInfo: React.FC<{file: File}> = ({file}) => {
	const [size, measurement] = convertToHumanReadable(file.size);
	const extension = file.name.split('.').pop();

	return (
		<div className={'flex items-center gap-4 w-full max-w-full min-w-0'}>
			<span
				className={
					'flex-none flex justify-center items-center h-12 w-12 font-semibold uppercase bg-black/[.04] rounded-md'
				}
			>
				{extension}
			</span>
			<h4 className={cn('flex-auto max-w-full w-full min-w-0', styles.fileName)} title={file.name}>
				{file.name}
			</h4>
			<span className={'flex-none text-black/25'}>
				{round(size)} {measurementTranslation[measurement]}
			</span>
		</div>
	);
};

type FileInputFormFieldProps = React.PropsWithChildren<{
	name?: string;
	acceptedFileTypes?: FileType[];
	onChange: React.ChangeEventHandler;
}>;

const FileInputFormField: React.FC<FileInputFormFieldProps> = ({name, acceptedFileTypes, onChange, children}) => {
	const id = useId();
	const accept = acceptedFileTypes?.join(',');

	return (
		<>
			<label htmlFor={id}>{children}</label>
			<input
				id={id}
				name={name}
				type={'file'}
				onChange={onChange}
				className={'absolute inset-0 w-full h-full'}
				accept={accept}
				hidden
			/>
		</>
	);
};

interface ReplaceFileProps {
	name?: string;
	acceptedFileTypes?: FileType[];
	placeholder?: string;
	onChange: React.ChangeEventHandler;
}

const ReplaceFile: React.FC<ReplaceFileProps> = ({name, acceptedFileTypes, placeholder, onChange}) => (
	<div className={'relative'}>
		<FileInputFormField name={name} acceptedFileTypes={acceptedFileTypes} onChange={onChange}>
			<Popover position={'top'} offset={40} handler={placeholder}>
				<span className={'cursor-pointer'}>
					<Icon name={'repeat'} className={'hover:bg-black/8'} circular />
				</span>
			</Popover>
		</FileInputFormField>
	</div>
);

interface RemoveFileProps {
	name?: string;
	placeholder?: string;
	onClick: () => void;
}

const RemoveFile: FC<RemoveFileProps> = ({placeholder, onClick}) => (
	<div className={'relative'}>
		<Popover position="top" offset={40} handler={placeholder}>
			<span className={'cursor-pointer'} onClick={onClick}>
				<Icon name={'cross'} className={'hover:bg-black/8'} circular />
			</span>
		</Popover>
	</div>
);

interface SelectedFileProps {
	name?: string;
	value: File;
	loading?: boolean;
	onChange: React.ChangeEventHandler;
	acceptedFileTypes?: FileType[];
	placeholder?: string;
	removable?: boolean;
	onRemove: () => void;
}

const SelectedFile: FC<SelectedFileProps> = ({
	name,
	value,
	loading,
	onChange,
	removable,
	acceptedFileTypes,
	placeholder,
	onRemove,
}) => (
	<div className={'flex items-center gap-4 h-20 w-full p-4'}>
		<div className={'flex-auto'}>
			<FileInfo file={value} />
		</div>
		<div className={'flex-none'}>
			{loading ? (
				<Icon name={'loading'} className={'animate-spin m-2.5'} />
			) : (
				<>
					{!removable ? (
						<ReplaceFile
							name={name}
							acceptedFileTypes={acceptedFileTypes}
							onChange={onChange}
							placeholder={placeholder}
						/>
					) : (
						<RemoveFile name={name} onClick={onRemove} placeholder={placeholder} />
					)}
				</>
			)}
		</div>
	</div>
);

const DragOverlay: FC<{hovered: boolean}> = ({hovered}) => (
	<Transition
		as={Fragment}
		show={hovered}
		enter={'transition'}
		enterFrom={'opacity-50 scale-95'}
		enterTo={'opacity-100 scale-100'}
		leave={'transition'}
		leaveFrom={'scale-100'}
		leaveTo={'scale-95 opacity-50'}
	>
		<div
			className={
				'absolute inset-0 grid place-content-center bg-black/[.04] rounded-md backdrop-blur-3xl cursor-grabbing'
			}
		>
			<div className={'grid gap-2 place-items-center'}>
				<Transition.Child enter={'transition'} enterFrom={'-translate-y-5'} enterTo={'translate-y-0'}>
					<span className={'grid pt-2'}>
						<Icon name={'attach'} />
					</span>
				</Transition.Child>
				<Transition.Child enter={'transition'} enterFrom={'translate-y-5'} enterTo={'translate-y-0'}>
					<p className={'text-black/30'}>Загрузить файл</p>
				</Transition.Child>
			</div>
		</div>
	</Transition>
);
